import api from 'api'
import {
  CACHE_TAG_RENTAL_FEES,
  CACHE_TAG_ACCOUNT_LIST,
  CACHE_TAG_ACCOUNTS_LINKED_TO_RENTAL_FEE,
  CACHE_TAG_RENTAL_FEES_LINKED_TO_ACCOUNT,
  CACHE_TAG_CONFIGURED_SERVICES_LIST,
} from 'api/cacheTagTypes'
import { transformSearchResponse } from 'utils/accountSearch'
import { get } from 'utils/lodash'

const extendedApi = api.injectEndpoints({
  endpoints: build => ({
    createRentalFee: build.mutation({
      query: body => ({
        url: '/api/v1/core/fee/rental',
        method: 'POST',
        body,
      }),
      invalidatesTags: [CACHE_TAG_RENTAL_FEES],
    }),
    updateRentalFee: build.mutation({
      query: ({ id, ...rest }) => ({
        url: `/api/v1/core/fee/rental/${id}`,
        method: 'PUT',
        body: { ...rest },
      }),
      invalidatesTags: [CACHE_TAG_RENTAL_FEES],
    }),
    getRentalFees: build.query({
      query: body => ({
        url: '/api/v1/core/fee/rental',
        method: 'GET',
        body,
      }),
      providesTags: [CACHE_TAG_RENTAL_FEES],
    }),
    getRentalFeeDetails: build.query({
      query: ({ id }) => ({
        url: `/api/v1/core/fee/rental/${id}`,
      }),
      providesTags: [CACHE_TAG_RENTAL_FEES],
    }),
    archiveRentalFee: build.mutation({
      query: ({ id }) => ({
        url: `/api/v1/core/fee/rental/${id}/archive`,
        method: 'PUT',
      }),
      invalidatesTags: [CACHE_TAG_RENTAL_FEES],
    }),
    restoreRentalFee: build.mutation({
      query: ({ id }) => ({
        url: `/api/v1/core/fee/rental/${id}/restore`,
        method: 'PUT',
      }),
      invalidatesTags: [CACHE_TAG_RENTAL_FEES],
    }),
    deleteRentalFee: build.mutation({
      query: ({ id }) => ({
        url: `/api/v1/core/fee/rental/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [CACHE_TAG_RENTAL_FEES],
    }),
    getAccountsLinkedToRentalFee: build.query({
      query: ({ id, includeTags = true, includeContacts = false, includeDeactivated = true }) => ({
        url: `/api/v1/core/fee/rental/${id}/accounts`,
        method: 'GET',
        params: { includeTags, includeContacts, includeDeactivated },
      }),
      transformResponse: data => {
        const { accounts } = transformSearchResponse({ results: get(data, 'accounts', []) })
        return { ...data, accounts }
      },
      providesTags: [CACHE_TAG_ACCOUNT_LIST, CACHE_TAG_ACCOUNTS_LINKED_TO_RENTAL_FEE],
    }),
    getRentalFeesLinkedToAccount: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/core/fee/rental/account/${accountId}/fees`,
        method: 'GET',
      }),
      providesTags: [CACHE_TAG_RENTAL_FEES_LINKED_TO_ACCOUNT],
    }),
    processRentalFeeDates: build.mutation({
      query: body => ({
        url: '/api/v1/core/fee/rental/process-rental-fee-dates',
        method: 'POST',
        body,
      }),
      invalidatesTags: [CACHE_TAG_CONFIGURED_SERVICES_LIST],
    }),
  }),
})

export const {
  useCreateRentalFeeMutation,
  useUpdateRentalFeeMutation,
  useGetRentalFeesQuery,
  useLazyGetRentalFeesQuery,
  useLazyGetRentalFeeDetailsQuery,
  useArchiveRentalFeeMutation,
  useRestoreRentalFeeMutation,
  useDeleteRentalFeeMutation,
  useLazyGetAccountsLinkedToRentalFeeQuery,
  useLazyGetRentalFeesLinkedToAccountQuery,
  useProcessRentalFeeDatesMutation,
} = extendedApi
